import { TRPCClientError } from "@trpc/client";

import type { UpgradePlanGroup } from "@/stores/upgrade-dialog-store";

/** The structured plan-limit cause the server exposes via `error.data.planLimit`. */
export type PlanLimitCause = {
  appCode: "PLAN_LIMIT_REACHED";
  feature: string;
  limit: number;
  current: number;
  planCode: string;
  upgradePlanCode?: UpgradePlanGroup;
};

type PlanLimitErrorData = {
  appCode?: string;
  planLimit?: PlanLimitCause;
};

/**
 * When a mutation trips a plan limit the server sets `error.data.appCode` to
 * "PLAN_LIMIT_REACHED" and `error.data.planLimit` to the full cause (see the
 * tRPC errorFormatter in packages/api/src/index.ts). Returns the cause when the
 * error is a plan-limit error, else null.
 */
export function getPlanLimitCause(cause: unknown): PlanLimitCause | null {
  if (!(cause instanceof TRPCClientError)) {
    return null;
  }

  // SAFETY: tRPC types `TRPCClientError.data` as the server's error shape,
  // which this app extends with the plan-limit fields in `errorFormatter`. The
  // `appCode` check on the next line is what proves those fields are present;
  // anything else returns `null`.
  const data = cause.data as PlanLimitErrorData | null | undefined;
  if (data?.appCode !== "PLAN_LIMIT_REACHED") {
    return null;
  }

  return data.planLimit ?? null;
}
